'use client'

import * as React from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ErrorDisplay } from '@/components/ui/error-display'
import { useToast } from '@/components/ui/use-toast'
import { useCreateProduct, type CreateProductInput } from '@/lib/api/products'
import { ProductInformationForm, ProductSettingsForm, ProductVariantsForm } from '@/components/form/product'
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils'
import { createProductSchema, type CreateProductFormData } from '../../products/create/validation'

interface CreateProductDialogProps {
    storeId: string
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Called with the new product id after a successful create. */
    onCreated: (productId: string) => void
}

// Seed one default variant so the variant section is present and a product
// can't be created without at least one variant.
const DEFAULT_VARIANT = {
    name: '',
    description: '',
    sku: '',
    imageUrl: '',
    grams: null,
    active: true,
    default: true,
    price: null,
    compareAtPrice: null,
    // Seed one default price (matches ProductPricesForm's add-price default).
    prices: [{ interval: 'month', intervalCount: 1, recurring: false, currencyOptions: {}, default: true }],
}

const DEFAULT_VALUES: CreateProductFormData = {
    name: '',
    description: null,
    unitLabel: null,
    accountingCode: null,
    taxCategory: null,
    mappedTaxCategoryId: null,
    active: true,
    isShippable: true,
    isTaxable: true,
    autoSync: true,
    variants: [DEFAULT_VARIANT],
}

/**
 * Inline product creation used by the club membership trigger. On success it
 * auto-assigns the new product as the membership trigger via `onCreated`.
 */
export const CreateProductDialog = ({ storeId, open, onOpenChange, onCreated }: CreateProductDialogProps) => {
    const { t } = useTranslation(['products', 'common'])
    const { toast } = useToast()

    const createProductMutation = useCreateProduct()
    const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null)

    const methods = useForm<CreateProductFormData>({
        defaultValues: DEFAULT_VALUES,
        resolver: zodResolver(createProductSchema),
        mode: 'onBlur',
    })
    const {
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = methods

    const handleOpenChange = (next: boolean) => {
        if (!next) {
            reset(DEFAULT_VALUES)
            setFormError(null)
        }
        onOpenChange(next)
    }

    const submit = async (data: CreateProductFormData) => {
        try {
            setFormError(null)
            const input: CreateProductInput = { ...data, storeId }
            const created = await createProductMutation.mutateAsync(input)

            toast({
                title: t('productCreated'),
                description: t('productCreatedSuccess'),
            })
            reset(DEFAULT_VALUES)
            onOpenChange(false)
            if (created?.id) onCreated(created.id)
        } catch (error) {
            const parsedError = parseTRPCError(error)
            setFormError(parsedError)
            if (!parsedError.isValidationError) {
                toast({
                    title: t('createError'),
                    description: parsedError.message,
                    variant: 'destructive',
                })
            }
        }
    }

    const isBusy = isSubmitting || createProductMutation.isPending

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="w-full sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle>{t('createProduct')}</DialogTitle>
                    <DialogDescription>
                        {t('createProductDescription')}
                    </DialogDescription>
                </DialogHeader>

                <FormProvider {...methods}>
                    <div className="max-h-[65vh] space-y-6 overflow-y-auto pr-1">
                        {formError && <ErrorDisplay error={formError} onDismiss={() => setFormError(null)} />}
                        <ProductInformationForm />
                        <ProductVariantsForm storeId={storeId} hideAddPrice hideRemovePrice />
                        <ProductSettingsForm />
                    </div>
                </FormProvider>

                <DialogFooter>
                    <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isBusy}>
                        {t('cancel')}
                    </Button>
                    <Button onClick={handleSubmit(submit)} disabled={isBusy}>
                        {isBusy
                            ? t('creating')
                            : t('createProduct')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
